import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { taskKeys } from '@/features/tasks/useTasks';
import type { Client, ClientDraft, Project, ProjectDraft } from './types';

const CLIENTS = ['clients'] as const;
const PROJECTS = ['projects'] as const;

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    // Client and project changes ripple into every task view: colours, names,
    // rates, and the billable default all read through them.
    qc.invalidateQueries({ queryKey: CLIENTS });
    qc.invalidateQueries({ queryKey: PROJECTS });
    qc.invalidateQueries({ queryKey: taskKeys.all });
  };
}

export function useClientList(includeArchived = false) {
  return useQuery<Client[]>({
    queryKey: [...CLIENTS, { includeArchived }],
    queryFn: () =>
      api<Client[]>(`/api/clients${includeArchived ? '?include_archived=true' : ''}`),
  });
}

export function useProjectList(includeArchived = false) {
  return useQuery<Project[]>({
    queryKey: [...PROJECTS, { includeArchived }],
    queryFn: () =>
      api<Project[]>(`/api/projects${includeArchived ? '?include_archived=true' : ''}`),
  });
}

export function useSaveClient() {
  const invalidate = useInvalidate();

  return useMutation<Client, ApiError, { id?: number } & ClientDraft>({
    mutationFn: ({ id, ...body }) =>
      id
        ? api<Client>(`/api/clients/${id}`, { method: 'PATCH', json: body })
        : api<Client>('/api/clients', { method: 'POST', json: body }),
    onSuccess: invalidate,
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteClient() {
  const invalidate = useInvalidate();

  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api<void>(`/api/clients/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    // The server refuses to delete the Personal client; surface that reason
    // rather than failing silently.
    onError: (error) => toast.error(error.message),
  });
}

export function useSaveProject() {
  const invalidate = useInvalidate();

  return useMutation<Project, ApiError, { id?: number } & ProjectDraft>({
    mutationFn: ({ id, ...body }) =>
      id
        ? api<Project>(`/api/projects/${id}`, { method: 'PATCH', json: body })
        : api<Project>('/api/projects', { method: 'POST', json: body }),
    onSuccess: invalidate,
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteProject() {
  const invalidate = useInvalidate();

  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api<void>(`/api/projects/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (error) => toast.error(error.message),
  });
}
